import { categories } from "@sobidogam/catalog";
import { normalizeStoreName } from "@sobidogam/domain";
import type { Receipt } from "@sobidogam/storage";
import { useState } from "react";
import { KoreanDateTimeField } from "./KoreanDateTimeField";
import { Sheet } from "./Sheet";

export interface RecordEdit {
	storeDisplay: string;
	totalAmount: number;
	categoryId: string;
	transactedAt: string;
}

function toKstInput(iso: string) {
	const time = Date.parse(iso);
	return Number.isFinite(time)
		? new Date(time + 9 * 60 * 60 * 1000).toISOString().slice(0, 16)
		: "";
}

/** #21: fix store, amount, category and time of a saved record. */
export function RecordEditor({
	receipt,
	onSave,
	onClose,
}: {
	receipt: Receipt;
	onSave: (patch: RecordEdit, rememberRule: boolean) => Promise<boolean>;
	onClose: () => void;
}) {
	const [store, setStore] = useState(receipt.storeDisplay);
	const [amount, setAmount] = useState(String(receipt.totalAmount));
	const [categoryId, setCategoryId] = useState(receipt.categoryId);
	const [at, setAt] = useState(toKstInput(receipt.transactedAt));
	const [remember, setRemember] = useState(true);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");
	const numeric = Number(amount);
	const storeError = !store.trim() ? "매장 이름을 적어 주세요." : "";
	const amountError =
		!Number.isSafeInteger(numeric) || numeric <= 0
			? "1원 이상으로 적어 주세요."
			: "";
	const timeError =
		at && Number.isFinite(Date.parse(`${at}:00+09:00`))
			? ""
			: "날짜와 시각을 골라 주세요.";
	const categoryChanged = categoryId !== receipt.categoryId;
	const categoryName =
		categories.find((category) => category.categoryId === categoryId)?.nameKo ??
		"기타";
	const save = async () => {
		if (storeError || amountError || timeError) return;
		setBusy(true);
		setError("");
		const ok = await onSave(
			{
				storeDisplay: store.trim(),
				totalAmount: numeric,
				categoryId,
				transactedAt: new Date(`${at}:00+09:00`).toISOString(),
			},
			categoryChanged && remember && !receipt.sensitiveMasked,
		).catch(() => false);
		setBusy(false);
		if (ok) onClose();
		else
			setError("고치지 못했어요. 입력은 그대로 두었으니 다시 시도해 주세요.");
	};
	return (
		<Sheet
			title="기록 고치기"
			onClose={onClose}
			footer={
				<button
					className="button full-width"
					type="button"
					disabled={busy || Boolean(storeError || amountError || timeError)}
					onClick={() => void save()}
				>
					{busy ? "저장 중…" : "고친 내용 저장"}
				</button>
			}
		>
			<div className="record-editor">
				<label>
					매장 이름
					<input
						value={store}
						maxLength={80}
						disabled={receipt.sensitiveMasked}
						aria-invalid={storeError ? "true" : undefined}
						onChange={(event) => setStore(event.target.value)}
					/>
					{receipt.sensitiveMasked ? (
						<small>건강 관련 업종은 업종 이름으로만 남겨요.</small>
					) : storeError ? (
						<small className="field-error">{storeError}</small>
					) : null}
				</label>
				<label>
					금액
					<input
						inputMode="numeric"
						value={amount}
						aria-invalid={amountError ? "true" : undefined}
						onChange={(event) =>
							setAmount(event.target.value.replace(/[^0-9]/g, ""))
						}
					/>
					{amountError ? (
						<small className="field-error">{amountError}</small>
					) : null}
				</label>
				<label>
					분류
					<select
						value={categoryId}
						disabled={receipt.sensitiveMasked}
						onChange={(event) => setCategoryId(event.target.value)}
					>
						{categories
							.filter(
								(category) =>
									!category.sensitive ||
									category.categoryId === receipt.categoryId,
							)
							.map((category) => (
								<option key={category.categoryId} value={category.categoryId}>
									{category.nameKo}
								</option>
							))}
					</select>
				</label>
				{categoryChanged && !receipt.sensitiveMasked ? (
					<label className="check-row">
						<input
							type="checkbox"
							checked={remember}
							onChange={(event) => setRemember(event.target.checked)}
						/>
						<span>
							다음에도 {normalizeStoreName(store).display || "이 매장"}은{" "}
							{categoryName}로
						</span>
					</label>
				) : null}
				<div className="date-field">
					날짜와 시각
					<KoreanDateTimeField value={at} onChange={setAt} />
					{timeError ? (
						<small className="field-error">{timeError}</small>
					) : null}
				</div>
				{error ? (
					<p className="form-error" role="alert">
						{error}
					</p>
				) : null}
			</div>
		</Sheet>
	);
}
